'use client';

import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { BiChevronDown } from 'react-icons/bi';

interface SelectInputProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'prefix' | 'onChange'> {
  prefix?: React.ReactNode;
  onChange?: (e: { target: { value: string } }) => void;
}

export const SelectInput: React.FC<SelectInputProps> = ({
  prefix,
  className = '',
  children,
  value,
  onChange,
  disabled,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const getTextFromChildren = (node: React.ReactNode): string => {
    if (typeof node === 'string' || typeof node === 'number') {
      return String(node);
    }
    if (Array.isArray(node)) {
      return node.map(getTextFromChildren).join('');
    }
    if (React.isValidElement(node)) {
      const props = node.props as { children?: React.ReactNode };
      if (props.children) {
        return getTextFromChildren(props.children);
      }
    }
    return '';
  };

  const options = React.Children.toArray(children)
    .filter((child): child is React.ReactElement => React.isValidElement(child) && child.type === 'option')
    .map((child) => {
      const childProps = child.props as { value?: string | number; children?: React.ReactNode; disabled?: boolean };
      return {
        value: String(childProps.value ?? ''),
        label: getTextFromChildren(childProps.children),
        disabled: !!childProps.disabled,
      };
    });

  const selectedOption = options.find((o) => o.value === value);
  const displayLabel = selectedOption?.label ?? '';
  const isPlaceholder = !selectedOption || selectedOption.disabled;

  const updateDropdownPosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const dropdownHeight = Math.min(options.length * 44, 220);

    if (spaceBelow >= dropdownHeight || spaceBelow >= spaceAbove) {
      setDropdownStyle({
        position: 'fixed',
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
        zIndex: 9999,
      });
    } else {
      setDropdownStyle({
        position: 'fixed',
        bottom: window.innerHeight - rect.top + 4,
        left: rect.left,
        width: rect.width,
        zIndex: 9999,
      });
    }
  };

  const handleToggle = () => {
    if (disabled) return;
    if (!isOpen) updateDropdownPosition();
    setIsOpen((prev) => !prev);
  };

  const handleSelect = (optValue: string, optDisabled: boolean) => {
    if (optDisabled) return;
    onChange?.({ target: { value: optValue } });
    setIsOpen(false);
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target as Node) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = (e: Event) => {
      if (dropdownRef.current?.contains(e.target as Node)) return;
      setIsOpen(false);
    };
    const handleResize = () => setIsOpen(false);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen]);

  const dropdown = isOpen ? (
    <div
      ref={dropdownRef}
      style={dropdownStyle}
      className="bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden"
    >
      <div className="max-h-55 overflow-y-auto">
        {options.map((opt) => {
          const isSelected = opt.value === value;
          const isDisabledOpt = opt.disabled;
          return (
            <div
              key={opt.value}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(opt.value, isDisabledOpt);
              }}
              className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors
                ${isDisabledOpt
                  ? 'text-gray-300 cursor-not-allowed'
                  : isSelected
                    ? 'bg-blue-50 text-blue-700 font-semibold cursor-pointer'
                    : 'text-gray-700 hover:bg-gray-50 cursor-pointer'
                }`}
            >
              <div className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 border transition-colors ${
                isSelected
                  ? 'bg-blue-500 border-blue-500'
                  : 'border-gray-300'
              }`}>
                {isSelected && (
                  <div className="w-1.5 h-1.5 rounded-full bg-white"></div>
                )}
              </div>
              <span className="truncate">{opt.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  ) : null;

  return (
    <div className={`relative ${className}`}>
      <div
        ref={triggerRef}
        onClick={handleToggle}
        className={`w-full flex items-center justify-between gap-2 
          ${prefix ? 'pl-9' : 'pl-3'} pr-3 py-2.5
          bg-gray-50 border rounded-xl text-sm transition-all cursor-pointer select-none
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-blue-300'}
          ${isOpen ? 'border-blue-400 ring-2 ring-blue-100' : 'border-gray-200'}
        `}
      >
        {prefix && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 flex items-center pointer-events-none">
            {prefix}
          </div>
        )}
        <span className={`truncate flex-1 ${isPlaceholder ? 'text-gray-400' : 'text-gray-700'}`}>
          {isPlaceholder ? (options.find(o => o.disabled)?.label ?? 'Select...') : displayLabel}
        </span>
        <BiChevronDown
          size={16}
          className={`text-gray-400 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </div>

      {typeof document !== 'undefined' && ReactDOM.createPortal(dropdown, document.body)}
    </div>
  );
};

